import type { ContentLocation, Direction, TurnMethod } from '@ilteducation/activity-stream-types';
import { detect } from 'detect-browser';
import { sendEvent } from './add-activity-stream-event';

const browser = detect();
const timestamp = () => new Date().toISOString();

export type BookEventParams = {
  partitionKey: string;
  sourceApplication: string;
};

const useBookEvents = ({ partitionKey, sourceApplication }: BookEventParams) => {
  const contentSessionStarted = (
    contentId: string,
    userId: string,
    roles: string[],
    organisations: string[],
    subscriptionType: string,
    locale: string,
    profileId?: string,
  ) => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'ContentSessionStartedEventV1',
      payload: {
        sessionId: partitionKey,
        timestamp: timestamp() as any,
        contentReference: { type: 'Book', id: contentId },
        userIdentity: {
          type: 'User',
          id: userId,
          profileId,
          roles,
          organisations,
        },
        deviceInfo: {
          type: 'Browser',
          name: browser?.name || 'unknown',
          version: browser?.version || '0.0.0',
        },
        appInfo: {
          type: 'Web',
          version: process.env.PACKAGE_VERSION || '0',
          language: locale,
        },
        service: subscriptionType,
      },
    });
  };
  const contentSessionEnded = () => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'ContentSessionEndedEventV1',
      payload: { sessionId: partitionKey, timestamp: timestamp() as any },
    });
  };
  const pageTurned = (direction: Direction, method: TurnMethod, location: ContentLocation, isFlowLayout: boolean) => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'PageTurnedEventV1',
      payload: { sessionId: partitionKey, timestamp: timestamp() as any, direction, method, location, isFlowLayout },
    });
  };
  const goToLocation = (location: ContentLocation) => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'GoToLocationEventV1',
      payload: { sessionId: partitionKey, timestamp: timestamp() as any, location },
    });
  };

  const audioPaused = () => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'AudioPausedEventV1',
      payload: { sessionId: partitionKey, timestamp: timestamp() as any },
    });
  };
  const audioStarted = () => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'AudioStartedEventV1',
      payload: { sessionId: partitionKey, timestamp: timestamp() as any },
    });
  };
  const audioRateSet = (rate: number) => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'AudioRateSetEventV1',
      payload: { sessionId: partitionKey, timestamp: timestamp() as any, rate },
    });
  };
  const audioLanguageChanged = (newLanguage: string) => {
    sendEvent({
      partitionKey,
      sourceApplication,
      type: 'AudioLanguageChangedEventV1',
      payload: { sessionId: partitionKey, timestamp: timestamp() as any, lang: newLanguage },
    });
  };

  return {
    contentSessionStarted,
    contentSessionEnded,
    pageTurned,
    goToLocation,
    audioPaused,
    audioStarted,
    audioRateSet,
    audioLanguageChanged,
  };
};

export default useBookEvents;
